import { AnimationFactory } from "../types";

export const createBloodChainAnimation: AnimationFactory = ({
                                                                ctx,
                                                                canvas,
                                                                startX,
                                                                startY,
                                                                endX,
                                                                endY,
                                                                startTime,
                                                                scale,
                                                            }) => {
    const SCALE = scale;

    const travelDuration = 420;
    const impactDuration = 220;
    const totalDuration = travelDuration + impactDuration;

    const chainGap = 16 * SCALE;
    const linkWidth = 12 * SCALE;
    const linkHeight = 7 * SCALE;

    type SplashParticle = {
        angle: number;
        speed: number;
        radius: number;
        drift: number;
    };

    let splashParticles: SplashParticle[] = [];

    const dx = endX - startX;
    const dy = endY - startY;
    const distance = Math.sqrt(dx * dx + dy * dy) || 1;
    const angle = Math.atan2(dy, dx);

    const normalX = -dy / distance;
    const normalY = dx / distance;

    const createSplashParticles = () => {
        splashParticles = [];
        const count = 14;

        for (let i = 0; i < count; i++) {
            splashParticles.push({
                angle: angle + (Math.random() - 0.5) * 1.4,
                speed: (30 + Math.random() * 45) * SCALE,
                radius: (1.5 + Math.random() * 3.5) * SCALE,
                drift: (Math.random() - 0.5) * 12 * SCALE,
            });
        }
    };

    const getChainPoint = (t: number, time: number) => {
        const wave1 = Math.sin(t * 10 - time * 0.02) * 5 * SCALE;
        const wave2 = Math.sin(t * 22 - time * 0.035) * 2.5 * SCALE;
        const tailSoftness = 0.35 + 0.65 * Math.sin(t * Math.PI);
        const offset = (wave1 + wave2) * tailSoftness;

        return {
            x: startX + dx * t + normalX * offset,
            y: startY + dy * t + normalY * offset,
        };
    };

    const drawLink = (
        x: number,
        y: number,
        rotation: number,
        alpha = 1,
        squash = 1
    ) => {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(rotation);

        // темна тінь
        ctx.strokeStyle = `rgba(35, 0, 0, ${alpha * 0.5})`;
        ctx.lineWidth = 4 * SCALE;
        ctx.beginPath();
        ctx.ellipse(
            1.5 * SCALE,
            1.5 * SCALE,
            linkWidth * 0.5,
            linkHeight * squash,
            0,
            0,
            Math.PI * 2
        );
        ctx.stroke();

        // основний металево-кривавий контур
        ctx.strokeStyle = `rgba(110, 10, 20, ${alpha})`;
        ctx.lineWidth = 3 * SCALE;
        ctx.beginPath();
        ctx.ellipse(
            0,
            0,
            linkWidth * 0.5,
            linkHeight * squash,
            0,
            0,
            Math.PI * 2
        );
        ctx.stroke();

        // внутрішній світліший відблиск
        ctx.strokeStyle = `rgba(180, 50, 60, ${alpha * 0.7})`;
        ctx.lineWidth = 1.2 * SCALE;
        ctx.beginPath();
        ctx.ellipse(
            -1 * SCALE,
            -0.5 * SCALE,
            linkWidth * 0.35,
            linkHeight * 0.7 * squash,
            0,
            0,
            Math.PI * 2
        );
        ctx.stroke();

        ctx.restore();
    };

    const drawBloodThread = (
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        alpha = 0.35
    ) => {
        ctx.strokeStyle = `rgba(120, 0, 0, ${alpha})`;
        ctx.lineWidth = 1.4 * SCALE;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
    };

    const drawImpactFlash = (progress: number) => {
        const alpha = 1 - progress;
        const radius = (18 + progress * 10) * SCALE;

        const grad = ctx.createRadialGradient(endX, endY, 0, endX, endY, radius);
        grad.addColorStop(0, `rgba(255,220,220,${alpha * 0.55})`);
        grad.addColorStop(0.35, `rgba(170,30,40,${alpha * 0.45})`);
        grad.addColorStop(1, `rgba(90,0,0,0)`);

        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(endX, endY, radius, 0, Math.PI * 2);
        ctx.fill();
    };

    return (now: number) => {
        const elapsed = now - startTime;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (elapsed >= totalDuration) {
            return false;
        }

        const travelProgress = Math.min(elapsed / travelDuration, 1);

        // Скільки довжини цепі вже "виросло"
        const visibleDistance = distance * travelProgress;

        // малюємо нитку/жилу під цепом
        const headPoint = getChainPoint(travelProgress, elapsed);
        drawBloodThread(startX, startY, headPoint.x, headPoint.y, 0.22);

        // малюємо ланки
        const linksCount = Math.ceil(visibleDistance / chainGap);

        for (let i = 0; i < linksCount; i++) {
            const d = i * chainGap;
            const t = Math.min(d / distance, travelProgress);

            const p = getChainPoint(t, elapsed);
            const nextT = Math.min(t + 0.02, travelProgress);
            const p2 = getChainPoint(nextT, elapsed);

            const localAngle = Math.atan2(p2.y - p.y, p2.x - p.x);
            const alternateRotation = i % 2 === 0 ? Math.PI / 4 : -Math.PI / 4;

            // хвіст трохи прозоріший
            const alpha = 0.45 + 0.55 * (i / Math.max(linksCount, 1));

            drawLink(
                p.x,
                p.y,
                localAngle + alternateRotation,
                alpha,
                1
            );

            // іноді тонка кривава перемичка між ланками
            if (i > 0) {
                const prevD = (i - 1) * chainGap;
                const prevT = Math.min(prevD / distance, travelProgress);
                const prev = getChainPoint(prevT, elapsed);
                drawBloodThread(prev.x, prev.y, p.x, p.y, 0.18);
            }
        }

        // голова цепі
        if (travelProgress < 1) {
            const pulse = 0.9 + Math.sin(elapsed * 0.04) * 0.08;

            const glow = ctx.createRadialGradient(
                headPoint.x,
                headPoint.y,
                0,
                headPoint.x,
                headPoint.y,
                16 * SCALE
            );
            glow.addColorStop(0, `rgba(255,160,160,${0.45 * pulse})`);
            glow.addColorStop(0.45, `rgba(170,20,30,${0.35 * pulse})`);
            glow.addColorStop(1, "rgba(100,0,0,0)");

            ctx.fillStyle = glow;
            ctx.beginPath();
            ctx.arc(headPoint.x, headPoint.y, 16 * SCALE, 0, Math.PI * 2);
            ctx.fill();

            // наконечник / гак
            ctx.save();
            ctx.translate(headPoint.x, headPoint.y);
            ctx.rotate(angle);

            ctx.fillStyle = "rgba(120, 15, 20, 0.95)";
            ctx.beginPath();
            ctx.moveTo(10 * SCALE, 0);
            ctx.lineTo(-4 * SCALE, -5 * SCALE);
            ctx.lineTo(-1 * SCALE, 0);
            ctx.lineTo(-4 * SCALE, 5 * SCALE);
            ctx.closePath();
            ctx.fill();

            ctx.restore();

            return true;
        }

        // impact phase
        const impactElapsed = elapsed - travelDuration;
        const impactProgress = Math.min(impactElapsed / impactDuration, 1);

        if (splashParticles.length === 0) {
            createSplashParticles();
        }

        // фінальний ривок всього ланцюга
        const recoil = Math.sin((1 - impactProgress) * Math.PI) * 8 * SCALE;
        const recoilX = Math.cos(angle) * recoil;
        const recoilY = Math.sin(angle) * recoil;

        const fullLinks = Math.ceil(distance / chainGap);
        for (let i = 0; i < fullLinks; i++) {
            const d = i * chainGap;
            const t = Math.min(d / distance, 1);

            const p = getChainPoint(t, elapsed);
            const nextT = Math.min(t + 0.02, 1);
            const p2 = getChainPoint(nextT, elapsed);

            const localAngle = Math.atan2(p2.y - p.y, p2.x - p.x);
            const alternateRotation = i % 2 === 0 ? Math.PI / 4 : -Math.PI / 4;

            const pullFactor = t * (1 - impactProgress);
            drawLink(
                p.x - recoilX * pullFactor,
                p.y - recoilY * pullFactor,
                localAngle + alternateRotation,
                1 - impactProgress * 0.35,
                1 + (1 - impactProgress) * 0.12
            );
        }

        drawImpactFlash(impactProgress);

        // криваві частки
        for (const particle of splashParticles) {
            const dist = particle.speed * impactProgress;
            const px =
                endX +
                Math.cos(particle.angle) * dist +
                normalX * particle.drift * impactProgress;
            const py =
                endY +
                Math.sin(particle.angle) * dist +
                normalY * particle.drift * impactProgress +
                impactProgress * impactProgress * 10 * SCALE;

            const alpha = 1 - impactProgress;

            ctx.fillStyle = `rgba(150, 20, 20, ${alpha * 0.75})`;
            ctx.beginPath();
            ctx.arc(px, py, particle.radius * (1 - impactProgress * 0.35), 0, Math.PI * 2);
            ctx.fill();

            ctx.fillStyle = `rgba(255, 180, 180, ${alpha * 0.25})`;
            ctx.beginPath();
            ctx.arc(
                px - 0.8 * SCALE,
                py - 0.8 * SCALE,
                particle.radius * 0.35,
                0,
                Math.PI * 2
            );
            ctx.fill();
        }

        return true;
    };
};